import React from 'react';
import { Button, Checkbox, InputNumber, Segmented, Select, Space, Typography } from 'antd';
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons';
import { DataTable } from '../../ui';
import type { DeliveryGroupDraft } from './deliveryGroupTypes';
import { buildUnloadingStopOptionsForGroup } from './groupScopeHelpers';
import { listRealLoadingPointsForGroup } from './executionBindingHelpers';
import type { ExecutionTopologyPreview } from './executionTopologyHelpers';
import type { MainStopFormFields } from './routeStopsSavePipeline';
import {
  isCargoCheckedInExecutionContext,
  type LoadingExecutionContext
} from './step4ExecutionState';
import type { AllocationEdgeDraft } from './executionDraftTypes';
import {
  addSplitEdgeForCargo,
  computeCargoAllocationSummary,
  createAllocationEdge,
  edgesForCargoAtLoading,
  removeEdgeFromList,
  upsertEdgeInList
} from './allocationEdgeDraft';
import { enrichUnloadingStopOption, renderUnloadOptionLabel } from './executionStopOptionLabels';
import { formatGroupTitle, ROUTE_UX } from './routeUxLabels';

const { Text } = Typography;

type Props = {
  multiEdgeEnabled?: boolean;
  allocationEdges?: AllocationEdgeDraft[];
  onAllocationEdgesChange?: (edges: AllocationEdgeDraft[]) => void;
  richUnloadLabels?: boolean;
  groups: DeliveryGroupDraft[];
  activeGroupUid: string | null;
  onActiveGroupChange: (uid: string) => void;
  executionContext: LoadingExecutionContext | null;
  onActiveLoadingStopChange: (stopUid: string) => void;
  cargoRows: any[];
  fromStopDraft: Record<number, string | undefined>;
  quantityDraft: Record<number, number>;
  toStopDraft: Record<number, string | undefined>;
  onToggleCargo: (cargoId: number, selected: boolean, defaultToStopUid: string) => void;
  onQuantityChange: (cargoId: number, qty: number) => void;
  onDestChange: (cargoId: number, toStopUid: string) => void;
  legacyStops: Array<{ position?: number; type?: string; parentLoadingPosition?: number | null; pointType?: string | null; pointId?: number | null; address?: string | null }>;
  mainUnloading: { position: number; pointType?: string | null; pointId?: number | null; type?: string };
  executionTopology?: ExecutionTopologyPreview | null;
  mainLoadingFields?: MainStopFormFields;
  mainUnloadingFields?: MainStopFormFields;
  applications: Array<{ id: number; internalNumber?: string | null; client?: { name?: string | null } | null }>;
  resolveWarehouseLabel?: (id: number) => string;
  resolveConsigneeLabel?: (id: number) => string;
};

function RouteCargoLoadingPanelInner(props: Props) {
  const {
    groups,
    activeGroupUid,
    onActiveGroupChange,
    executionContext,
    onActiveLoadingStopChange,
    cargoRows,
    fromStopDraft,
    quantityDraft,
    toStopDraft,
    onToggleCargo,
    onQuantityChange,
    onDestChange,
    legacyStops,
    mainUnloading,
    executionTopology,
    mainLoadingFields,
    mainUnloadingFields,
    applications,
    resolveWarehouseLabel,
    resolveConsigneeLabel,
    richUnloadLabels = false,
    multiEdgeEnabled = false
  } = props;

  const activeGroup =
    groups.find((g) => g.uid === activeGroupUid) ?? groups[0] ?? null;

  const groupOptions = React.useMemo(
    () =>
      groups.map((g, index) => {
        const app = g.applicationId ? applications.find((a) => a.id === g.applicationId) : undefined;
        return {
          value: g.uid,
          label: formatGroupTitle({
            applicationNumber: app?.internalNumber ?? null,
            clientName: app?.client?.name ?? null,
            index
          })
        };
      }),
    [applications, groups]
  );

  const loadingOptions = React.useMemo(
    () => (activeGroup ? listRealLoadingPointsForGroup(activeGroup, executionTopology) : []),
    [activeGroup, executionTopology]
  );

  const normalizedLegacyStops = React.useMemo(
    () =>
      legacyStops.map((s, idx) => ({
        position: s.position ?? idx + 1,
        type: String(s.type ?? 'unloading'),
        parentLoadingPosition: s.parentLoadingPosition,
        pointType: s.pointType,
        pointId: s.pointId,
        address: s.address
      })),
    [legacyStops]
  );

  const mainUnloadingLegacy = React.useMemo(
    () => ({
      ...mainUnloading,
      type: String(mainUnloading.type ?? 'unloading')
    }),
    [mainUnloading]
  );

  const labelContext = React.useMemo(
    () => ({
      resolveWarehouseName: (id: number) => {
        const raw = resolveWarehouseLabel?.(id);
        if (!raw) return undefined;
        return raw.replace(/^Склад\s*[—-]\s*/i, '').trim() || raw;
      },
      resolveConsigneeName: (id: number) => {
        const raw = resolveConsigneeLabel?.(id);
        if (!raw) return undefined;
        return raw.replace(/^Получатель\s*[—-]\s*/i, '').trim() || raw;
      }
    }),
    [resolveConsigneeLabel, resolveWarehouseLabel]
  );

  const activeAppNumber = React.useMemo(() => {
    if (!activeGroup?.applicationId) return null;
    const app = applications.find((a) => a.id === activeGroup.applicationId);
    return app?.internalNumber ?? null;
  }, [activeGroup, applications]);

  const unloadingOptions = React.useMemo(() => {
    if (!activeGroup) return [];
    const base = buildUnloadingStopOptionsForGroup({
      group: activeGroup,
      topology: executionTopology,
      mainLoading: mainLoadingFields,
      mainUnloading: mainUnloadingFields,
      labelContext,
      mainUnloadingLegacy,
      legacyStops: normalizedLegacyStops,
      resolveWarehouseLabel,
      resolveConsigneeLabel
    });
    if (!richUnloadLabels) return base;
    return base.map((o) => {
      const stop = activeGroup.unloadingStops.find((s) => s.uid === o.stopUid);
      if (!stop) return o;
      return enrichUnloadingStopOption({
        stopUid: o.stopUid,
        groupUid: o.groupUid,
        position: o.position,
        stop,
        labelContext,
        applicationNumber: activeAppNumber
      });
    });
  }, [
    activeGroup,
    activeAppNumber,
    executionTopology,
    labelContext,
    mainLoadingFields,
    mainUnloadingFields,
    mainUnloadingLegacy,
    normalizedLegacyStops,
    resolveConsigneeLabel,
    resolveWarehouseLabel,
    richUnloadLabels
  ]);

  const defaultToStopUid = unloadingOptions[0]?.stopUid ?? '';

  const unloadingSelectOptions = React.useMemo(
    () =>
      unloadingOptions.map((o) => ({
        value: o.stopUid,
        label: richUnloadLabels ? renderUnloadOptionLabel(o) : o.label
      })),
    [richUnloadLabels, unloadingOptions]
  );

  const loadingSelectOptions = React.useMemo(
    () => loadingOptions.map((o) => ({ value: o.stopUid, label: o.label })),
    [loadingOptions]
  );

  const loadingStopUid = executionContext?.stopUid ?? '';

  const updateEdges = (next: AllocationEdgeDraft[]) => {
    if (multiEdgeEnabled) {
      props.onAllocationEdgesChange?.(next);
    }
  };

  const renderMultiEdgeTable = () => {
    if (!multiEdgeEnabled || !props.multiEdgeEnabled || !loadingStopUid) return null;

    const edges = props.allocationEdges ?? [];
    const rows: Array<{
      key: string;
      rowType: 'cargo-header' | 'edge';
      cargoId: number;
      edge?: AllocationEdgeDraft;
      cargoRecord?: any;
    }> = [];

    for (const record of cargoRows) {
      const cargoId = Number(record.id);
      const cargoEdges = edgesForCargoAtLoading({
        edges,
        cargoId,
        loadingStopUid
      });

      rows.push({ key: `cargo-${cargoId}`, rowType: 'cargo-header', cargoId, cargoRecord: record });

      if (cargoEdges.length === 0) {
        rows.push({
          key: `edge-new-${cargoId}`,
          rowType: 'edge',
          cargoId,
          cargoRecord: record,
          edge: createAllocationEdge({
            cargoId,
            fromStopUid: loadingStopUid,
            toStopUid: defaultToStopUid,
            quantity: 0
          })
        });
      } else {
        for (const edge of cargoEdges) {
          rows.push({ key: edge.edgeUid, rowType: 'edge', cargoId, cargoRecord: record, edge });
        }
      }
    }

    const columns = [
      {
        title: '',
        width: 40,
        render: (_: unknown, row: (typeof rows)[0]) => {
          if (row.rowType === 'cargo-header') return null;
          const edge = row.edge!;
          const checked = edge.quantity > 0;
          return (
            <Checkbox
              checked={checked}
              onChange={(e) => {
                const qty = e.target.checked
                  ? Number(row.cargoRecord?.availableQuantity ?? row.cargoRecord?.quantity ?? 1) || 1
                  : 0;
                updateEdges(
                  upsertEdgeInList(edges, { ...edge, quantity: e.target.checked ? Math.max(1, qty) : 0 })
                );
              }}
            />
          );
        }
      },
      {
        title: ROUTE_UX.cargoName,
        key: 'name',
        render: (_: unknown, row: (typeof rows)[0]) => {
          if (row.rowType === 'cargo-header') {
            const name = row.cargoRecord?.name ?? `Груз #${row.cargoId}`;
            const total = Number(row.cargoRecord?.availableQuantity ?? row.cargoRecord?.quantity ?? 0);
            const summary = computeCargoAllocationSummary({
              cargoId: row.cargoId,
              edges,
              totalQuantity: total
            });
            const cargoEdges = edgesForCargoAtLoading({
              edges,
              cargoId: row.cargoId,
              loadingStopUid
            });
            const resolveDestLabel = (toStopUid: string) => {
              const opt = unloadingOptions.find((o) => o.stopUid === toStopUid);
              return opt?.label?.trim() || '—';
            };
            return (
              <div>
                <Text strong>{name}</Text>
                <div>
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    Всего: {summary.total} · Распределено: {summary.distributed} · Осталось:{' '}
                    {summary.remaining}
                  </Text>
                </div>
                {cargoEdges.length > 0 && summary.distributed > 0 ? (
                  <div style={{ marginTop: 4, paddingLeft: 4 }}>
                    {cargoEdges.map((edge, idx) => {
                      const branch = idx === cargoEdges.length - 1 ? '└' : '├';
                      const qty = Number(edge.quantity) || 0;
                      if (qty <= 0) return null;
                      return (
                        <div key={edge.edgeUid}>
                          <Text type="secondary" style={{ fontSize: 12 }}>
                            {branch} {qty} → {resolveDestLabel(edge.toStopUid)}
                          </Text>
                        </div>
                      );
                    })}
                  </div>
                ) : null}
              </div>
            );
          }
          return <Text type="secondary">↳</Text>;
        }
      },
      {
        title: ROUTE_UX.cargoQty,
        key: 'qty',
        width: 120,
        render: (_: unknown, row: (typeof rows)[0]) => {
          if (row.rowType !== 'edge' || !row.edge) return null;
          const edge = row.edge;
          const total = Number(row.cargoRecord?.availableQuantity ?? row.cargoRecord?.quantity ?? 0);
          const summary = computeCargoAllocationSummary({
            cargoId: row.cargoId,
            edges,
            totalQuantity: total
          });
          const maxForLine = summary.remaining + edge.quantity;
          return (
            <InputNumber
              min={0}
              max={maxForLine > 0 ? maxForLine : undefined}
              value={edge.quantity}
              onChange={(v) => {
                updateEdges(
                  upsertEdgeInList(edges, { ...edge, quantity: Math.max(0, Number(v ?? 0)) })
                );
              }}
              style={{ width: 100 }}
            />
          );
        }
      },
      {
        title: ROUTE_UX.cargoDest,
        key: 'dest',
        width: 340,
        render: (_: unknown, row: (typeof rows)[0]) => {
          if (row.rowType !== 'edge' || !row.edge) return null;
          const edge = row.edge;
          return (
            <Select
              style={{ width: '100%' }}
              placeholder="Выберите точку выгрузки"
              value={edge.toStopUid || undefined}
              options={unloadingSelectOptions}
              optionLabelProp="label"
              onChange={(v) => {
                updateEdges(upsertEdgeInList(edges, { ...edge, toStopUid: String(v) }));
              }}
            />
          );
        }
      },
      {
        title: '',
        key: 'actions',
        width: 200,
        render: (_: unknown, row: (typeof rows)[0]) => {
          if (row.rowType === 'cargo-header') {
            const total = Number(row.cargoRecord?.availableQuantity ?? row.cargoRecord?.quantity ?? 0);
            const summary = computeCargoAllocationSummary({
              cargoId: row.cargoId,
              edges,
              totalQuantity: total
            });
            if (summary.remaining <= 0) return null;
            return (
              <Button
                type="link"
                size="small"
                icon={<PlusOutlined />}
                onClick={() => {
                  updateEdges(
                    addSplitEdgeForCargo({
                      edges,
                      cargoId: row.cargoId,
                      fromStopUid: loadingStopUid,
                      defaultToStopUid,
                      totalQuantity: total
                    })
                  );
                }}
              >
                Разделить
              </Button>
            );
          }
          const edge = row.edge!;
          const cargoEdges = edgesForCargoAtLoading({
            edges,
            cargoId: row.cargoId,
            loadingStopUid
          });
          return (
            <Space>
              {cargoEdges.length > 1 ? (
                <Button
                  type="link"
                  size="small"
                  danger
                  icon={<DeleteOutlined />}
                  onClick={() => updateEdges(removeEdgeFromList(edges, edge.edgeUid))}
                >
                  Удалить строку
                </Button>
              ) : null}
            </Space>
          );
        }
      }
    ];

    return (
      <DataTable
        key={`multi-${activeGroupUid}-${loadingStopUid}`}
        size="small"
        pagination={false}
        dataSource={rows}
        columns={columns}
      />
    );
  };

  const tableDataSource = React.useMemo(
    () => cargoRows.map((r) => ({ ...r, key: r.id })),
    [cargoRows]
  );

  const legacyColumns = React.useMemo(
    () => [
      {
        title: '',
        width: 40,
        render: (_: unknown, record: any) => {
          const id = Number(record.id);
          const checked =
            executionContext != null
              ? isCargoCheckedInExecutionContext(id, fromStopDraft, quantityDraft, executionContext)
              : false;
          return (
            <Checkbox
              checked={checked}
              onChange={(e) => onToggleCargo(id, e.target.checked, defaultToStopUid)}
            />
          );
        }
      },
      { title: ROUTE_UX.cargoName, dataIndex: 'name', key: 'name' },
      {
        title: ROUTE_UX.cargoWeight,
        key: 'weight',
        width: 100,
        render: (_: unknown, r: any) => r.weight ?? r.totalWeight ?? '—'
      },
      {
        title: ROUTE_UX.cargoQty,
        key: 'qty',
        width: 120,
        render: (_: unknown, record: any) => {
          const id = Number(record.id);
          const checked =
            executionContext != null
              ? isCargoCheckedInExecutionContext(id, fromStopDraft, quantityDraft, executionContext)
              : false;
          const max = Number(record.availableQuantity ?? record.quantity ?? 0);
          return (
            <InputNumber
              min={0}
              max={max > 0 ? max : undefined}
              disabled={!checked}
              value={quantityDraft[id] ?? (checked ? (max > 0 ? max : 1) : 0)}
              onChange={(v) => onQuantityChange(id, Number(v ?? 0))}
              style={{ width: 100 }}
            />
          );
        }
      },
      {
        title: ROUTE_UX.cargoDest,
        key: 'dest',
        width: 320,
        render: (_: unknown, record: any) => {
          const id = Number(record.id);
          const checked =
            executionContext != null
              ? isCargoCheckedInExecutionContext(id, fromStopDraft, quantityDraft, executionContext)
              : false;
          const destValue = toStopDraft[id];
          return (
            <Select
              style={{ width: '100%' }}
              disabled={!checked}
              placeholder="Выберите точку выгрузки"
              value={destValue ?? (checked ? defaultToStopUid : undefined)}
              options={unloadingSelectOptions}
              onChange={(v) => onDestChange(id, String(v))}
            />
          );
        }
      }
    ],
    [
      defaultToStopUid,
      executionContext,
      fromStopDraft,
      onDestChange,
      onQuantityChange,
      onToggleCargo,
      quantityDraft,
      toStopDraft,
      unloadingSelectOptions
    ]
  );

  return (
    <Space direction="vertical" style={{ width: '100%' }} size={12}>
      <div>
        <Text type="secondary">{ROUTE_UX.cargoGroupNav}</Text>
        <Segmented
          style={{ marginTop: 6, flexWrap: 'wrap' }}
          value={activeGroupUid ?? groups[0]?.uid}
          options={groupOptions}
          onChange={(val) => onActiveGroupChange(String(val))}
        />
      </div>
      {loadingOptions.length > 0 ? (
        <div>
          <Text type="secondary">{ROUTE_UX.cargoLoadingNav}</Text>
          <Select
            style={{ width: '100%', marginTop: 6, maxWidth: 520 }}
            value={executionContext?.stopUid ?? loadingOptions[0]?.stopUid}
            options={loadingSelectOptions}
            onChange={(v) => onActiveLoadingStopChange(String(v))}
          />
        </div>
      ) : (
        <Text type="secondary">Добавьте точку погрузки на шаге 1</Text>
      )}
      <Text type="secondary">{ROUTE_UX.cargoSelectHint}</Text>
      {multiEdgeEnabled && props.allocationEdges ? (
        renderMultiEdgeTable()
      ) : (
        <DataTable
          key={`${activeGroupUid ?? 'g'}-${executionContext?.stopUid ?? 's'}`}
          size="small"
          pagination={false}
          dataSource={tableDataSource}
          columns={legacyColumns}
        />
      )}
    </Space>
  );
}

export const RouteCargoLoadingPanel = React.memo(RouteCargoLoadingPanelInner);
